import ky from 'ky';
import qs from 'query-string';
import {
  AUTH_KEY,
  authToken,
  notifySessionExpired,
  sessionIsDead,
  sessionIsExpiring,
} from '@lib/session';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Params {}

export default async function api<T>(
  path: string,
  params: any = undefined,
  opts: any = {},
): Promise<T | undefined> {
  opts.method = opts.method || 'get';
  const token = authToken();

  // `false` meant "wait forever". A request that never settles leaves the
  // caller's await hanging, which is how the dashboard could sit on its
  // loading spinner indefinitely instead of reporting a problem.
  opts.timeout = opts.timeout ?? 30000;
  opts.retry = opts.retry ?? 0;

  if (opts.method !== 'get') {
    opts.json = params;
  } else if (params) {
    path = `${path}?${qs.stringify(params)}`;
  }

  opts.headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
    'Access-Control-Allow-Headers':
      'X-Requested-With, content-type, Authorization',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH, OPTIONS',
    'Access-Control-Allow-Origin': '*',
    'Referrer-Policy': 'no-referrer',
  };
  if (token) {
    opts.headers.auth = token;
  }

  const apiUrl = process.env.REACT_APP_API_URL;
  let response;

  try {
    response = await ky(`${apiUrl}/api/${path}`, opts);
  } catch (e: any) {
    const status = e?.response?.status;

    // A dead session must end as a login prompt, not as an error toast. Every
    // request carrying the stale token fails, and previously each failure
    // became an unhandled rejection that the store rendered as
    // "Error detected" — leaving the user staring at a broken dashboard with
    // no way back short of clearing site data.
    //
    // 403 is checked rather than assumed: the API also answers 403 for
    // "signed in, but not yours". See session.ts.
    if ((status === 401 || status === 403) && token && !sessionIsExpiring()) {
      // Deliberately NOT awaited. The caller's failure should surface straight
      // away so the app can fall through to the login form; the probe only
      // decides whether to also tear the stored session down. Awaiting it made
      // every expired-session load wait for a second round trip — up to the
      // probe's 10s timeout — staring at a spinner before the login form
      // appeared. It also made the teardown slow enough to flake under load.
      void sessionIsDead(apiUrl).then(dead => {
        if (dead) notifySessionExpired();
      });
    }

    throw e;
  }

  try {
    const resp = await response.json();

    if (resp?.auth) {
      localStorage.setItem(AUTH_KEY, resp.auth);
    }

    return resp;
  } catch (e) {
    if (!response.ok) {
      throw e;
    }
  }
}

export function get<T>(path: string, opts: RequestInit = {}) {
  return api<T>(path, undefined, opts);
}

export function post<T>(path: string, params: Params, opts: RequestInit = {}) {
  opts.method = 'post';
  return api<T>(path, params, opts);
}

export function put<T>(path: string, params: Params, opts: RequestInit = {}) {
  opts.method = 'put';
  return api<T>(path, params, opts);
}

export function destroy<T>(path: string, opts: RequestInit = {}) {
  opts.method = 'delete';
  return api<T>(path, null, opts);
}
